import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiUrl } from '../utils/apiBase';
import { signupPhase, type SignupPhase } from './signup-window';
import { exclusiveConflictMessage, exclusiveGroupNames } from './exclusive-note';
import { PlayerAutocomplete, type PlayerLite } from '../leagues/player-autocomplete';
import { NewPlayerInline } from '../leagues/new-player-inline';
import { RatingChip } from '../leagues/rating-chip';

const DOUBLES_DISCIPLINES = ['DOUBLES', 'DOUBLES_M', 'DOUBLES_F', 'MIXED'];

function isDoubles(discipline: string): boolean {
  return DOUBLES_DISCIPLINES.includes(discipline);
}

const DISCIPLINE_LABELS: Record<string, string> = {
  SINGLES: 'Singiel',
  SINGLES_M: 'Singiel mężczyzn',
  SINGLES_F: 'Singiel kobiet',
  DOUBLES: 'Debel',
  DOUBLES_M: 'Debel mężczyzn',
  DOUBLES_F: 'Debel kobiet',
  MIXED: 'Mikst',
  CUSTOM: 'Własna',
};

type Outcome = {
  status: 'CONFIRMED' | 'PENDING' | 'WAITLIST';
  waitlistReason: 'DEADLINE' | 'CATEGORY_LIMIT' | null;
};

function successMessage(outcome: Outcome): string {
  if (outcome.status === 'CONFIRMED') {
    return 'Zapis widnieje już na liście uczestników.';
  }

  if (outcome.status === 'PENDING') {
    return 'Organizator musi jeszcze potwierdzić zgłoszenie — pojawi się na liście po akceptacji.';
  }

  if (outcome.waitlistReason === 'CATEGORY_LIMIT') {
    return 'Masz już komplet kategorii dozwolonych w tym turnieju, więc to zgłoszenie trafiło na listę oczekujących. Organizator dobierze Cię, jeśli zwolni się miejsce.';
  }

  return 'Jesteś na liście oczekujących. Organizator odezwie się, jeśli zwolni się miejsce — zostaw kontakt, żeby mógł to zrobić.';
}

const ERROR_MESSAGES: Record<string, string> = {
  REGISTRATION_CLOSED: 'Zapisy na ten turniej są zamknięte.',
  ALREADY_REGISTERED: 'Ta osoba jest już zapisana w tej kategorii.',
  CATEGORY_FULL: 'Limit miejsc w tej kategorii został wyczerpany.',
  PARTNER_REQUIRED: 'Debel wymaga dwóch zawodników.',
  PARTNER_NOT_ALLOWED: 'Ta kategoria jest singlowa — podaj jednego zawodnika.',
  SAME_PLAYER_TWICE: 'Wskaż dwie różne osoby.',
  PLAYER_NOT_FOUND: 'Nie znaleziono wskazanego zawodnika.',
  INVALID_INPUT: 'Uzupełnij poprawnie dane zawodników.',
};

type Entrant =
  | { playerId: number; name: string; rating: number | null }
  | { newPlayer: true; name: string };

function entrantPayload(entrant: Entrant): { playerId: number } | { name: string } {
  if ('playerId' in entrant) {
    return { playerId: entrant.playerId };
  }

  return { name: entrant.name };
}

function entrantFromPick(picked: PlayerLite | { newPlayer: true; query: string }, doubles: boolean): Entrant {
  if ('newPlayer' in picked) {
    return { newPlayer: true, name: picked.query };
  }

  return {
    playerId: picked.id,
    name: `${picked.firstName} ${picked.lastName}`,
    rating: (doubles ? picked.doublesRating : picked.singlesRating) ?? null,
  };
}

interface EntrantFieldProps {
  label: string;
  doubles: boolean;
  self: boolean;
  testIdPrefix: string;
  value: Entrant | null;
  onChange: (entrant: Entrant | null) => void;
}

function EntrantField({ label, doubles, self, testIdPrefix, value, onChange }: EntrantFieldProps): JSX.Element {
  const who = self ? 'mnie' : 'go';

  return (
    <div className="form-field">
      <label>{label}</label>
      {value ? (
        <div className="guest-player-selected" data-testid={`${testIdPrefix}-selected`}>
          <span className="guest-player-selected__name">{value.name}</span>
          {'playerId' in value && <RatingChip tag={doubles ? 'D' : 'S'} value={value.rating} />}
          <button
            type="button"
            className="btn-link"
            onClick={() => onChange(null)}
            data-testid={`${testIdPrefix}-clear`}
          >
            zmień
          </button>
        </div>
      ) : (
        <>
          <PlayerAutocomplete
            placeholder={self ? 'Wpisz swoje imię i nazwisko…' : 'Szukaj partnera…'}
            ratingKind={doubles ? 'doubles' : 'singles'}
            createLabel={`Nie ma ${who} na liście — zgłoś nowego zawodnika`}
            onSelect={(picked) => onChange(entrantFromPick(picked, doubles))}
          />
          <NewPlayerInline
            toggleLabel={`Nie ma ${who} w bazie? Zgłoś nowego zawodnika`}
            submitLabel="Użyj tych danych"
            testIdPrefix={`${testIdPrefix}-new`}
            onSubmit={(fullName) => onChange({ newPlayer: true, name: fullName })}
          />
        </>
      )}
    </div>
  );
}

interface CategoryMeta {
  id: number;
  name: string;
  discipline: string;
  maxPlayers: number | null;
  registrationCount: number;
  waitlistCount: number;
}

const RegistrationForm: React.FC = () => {
  const { id: tournamentId, categoryId } = useParams<{ id: string; categoryId: string }>();
  const [category, setCategory] = useState<CategoryMeta | null>(null);
  const [exclusiveWith, setExclusiveWith] = useState<string[]>([]);
  const [phase, setPhase] = useState<SignupPhase>('OPEN');
  const [loadError, setLoadError] = useState<string | null>(null);

  const [player, setPlayer] = useState<Entrant | null>(null);
  const [partner, setPartner] = useState<Entrant | null>(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState<Outcome | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!tournamentId) {
      return;
    }

    fetch(apiUrl(`/api/public/events/${tournamentId}`))
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json();
      })
      .then((data: {
        status: string;
        registrationEndDate: string | null;
        categories: CategoryMeta[];
        exclusiveCategoryGroups?: number[][];
      }) => {
        const found = data.categories.find((entry) => entry.id === Number(categoryId));

        if (!found) {
          setLoadError('Nie znaleziono kategorii.');

          return;
        }

        setCategory(found);
        setExclusiveWith(exclusiveGroupNames(data.exclusiveCategoryGroups, found.id, data.categories));
        setPhase(signupPhase(data, new Date()));
      })
      .catch(() => setLoadError('Nie udało się załadować kategorii.'));
  }, [tournamentId, categoryId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!category || !tournamentId) {
      return;
    }

    const doubles = isDoubles(category.discipline);

    if (!player || (doubles && !partner)) {
      setSubmitError(doubles ? 'Uzupełnij dane obu zawodników.' : 'Wskaż zawodnika.');

      return;
    }

    setSubmitError(null);
    setSubmitting(true);

    try {
      const response = await fetch(
        apiUrl(`/api/public/events/${tournamentId}/categories/${category.id}/registrations`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            player: entrantPayload(player),
            partner: doubles && partner ? entrantPayload(partner) : undefined,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
          }),
        },
      );

      if (response.status === 201) {
        const data = await response.json() as { registration: Outcome };
        setConfirmed({
          status: data.registration.status,
          waitlistReason: data.registration.waitlistReason,
        });

        return;
      }

      const data = await response.json().catch(() => ({ error: '' })) as {
        error?: string;
        conflictingCategory?: { name?: string };
      };
      setSubmitError(
        exclusiveConflictMessage(data)
          ?? ERROR_MESSAGES[data.error ?? '']
          ?? 'Nieoczekiwany błąd. Spróbuj ponownie.',
      );
    } catch {
      setSubmitError('Błąd sieci. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return <div className="reg-form-error">{loadError}</div>;
  }

  if (!category) {
    return <div className="reg-form-loading">Ładowanie...</div>;
  }

  if (confirmed) {
    return (
      <div className="reg-form-success">
        <h2>{confirmed.status === 'CONFIRMED' ? 'Zapisano!' : 'Zgłoszenie wysłane'}</h2>
        <p>{successMessage(confirmed)}</p>
        <Link to={`/events/${tournamentId}/participants`}>Zobacz listę uczestników</Link>
      </div>
    );
  }

  const doubles = isDoubles(category.discipline);
  const waitlisted = phase === 'WAITLIST';
  const seatsLeft = category.maxPlayers === null ? null : category.maxPlayers - category.registrationCount;

  return (
    <div className="reg-form">
      <Link to={`/events/${tournamentId}`} className="reg-form-back">
        ← Powrót
      </Link>
      <h1>{waitlisted ? 'Lista oczekujących' : 'Zapisz się'}</h1>
      <h2 className="reg-form-category">{category.name}</h2>
      <div className="reg-form-discipline">
        {DISCIPLINE_LABELS[category.discipline] ?? category.discipline}
        {!waitlisted && seatsLeft !== null && (
          <span className="reg-form-seats"> · wolnych miejsc: {Math.max(seatsLeft, 0)}</span>
        )}
        {waitlisted && category.waitlistCount > 0 && (
          <span className="reg-form-seats"> · oczekujących: {category.waitlistCount}</span>
        )}
      </div>
      {exclusiveWith.length > 0 && (
        <p className="reg-form-exclusive-note">
          Zapis tylko do jednej z: {exclusiveWith.join(', ')}. Osoba zapisana już w jednej z nich nie
          wejdzie do drugiej.
        </p>
      )}
      {waitlisted && (
        <p className="reg-form-waitlist-note">
          Termin zapisów już minął. Zgłoszenie trafi na listę oczekujących — organizator odezwie
          się, jeśli zwolni się miejsce.
        </p>
      )}
      <form onSubmit={handleSubmit} noValidate autoComplete="off" className="guest-signup-form">
        <EntrantField
          label={doubles ? 'Zawodnik 1' : 'Zawodnik'}
          doubles={doubles}
          self
          testIdPrefix="reg-player"
          value={player}
          onChange={setPlayer}
        />
        {doubles && (
          <EntrantField
            label="Zawodnik 2"
            doubles={doubles}
            self={false}
            testIdPrefix="reg-partner"
            value={partner}
            onChange={setPartner}
          />
        )}
        <div className="form-field">
          <label htmlFor="reg-email">E-mail (opcjonalnie)</label>
          <input
            id="reg-email"
            type="email"
            autoComplete="email"
            placeholder="jan@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="reg-phone">Telefon (opcjonalnie)</label>
          <input
            id="reg-phone"
            type="tel"
            autoComplete="tel"
            placeholder="+48 123 456 789"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        {submitError && <p className="auth-error" data-testid="reg-submit-error">{submitError}</p>}
        <div className="guest-signup-form__actions">
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="reg-submit">
            {submitting ? 'Wysyłanie…' : waitlisted ? 'Dopisz się do listy oczekujących' : 'Zapisz się'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;
